import { functionalUpdate, type Updater } from '@tanstack/react-table';

export type LegacyColumnIdMap = ReadonlyMap<string, string>;

export const emptyLegacyColumnIdMap: LegacyColumnIdMap = new Map();

type ColumnWithLegacyIds = { id?: string; meta?: { legacyIds?: readonly string[] } };

export function getLegacyColumnIdMap(
  columns: ReadonlyArray<ColumnWithLegacyIds | undefined>,
): LegacyColumnIdMap {
  const currentIds = new Set<string>();
  for (const column of columns) {
    if (column?.id) currentIds.add(column.id);
  }

  const map = new Map<string, string>();
  const ambiguous = new Set<string>();
  for (const column of columns) {
    if (!column?.id) continue;
    for (const legacyId of column.meta?.legacyIds ?? []) {
      if (currentIds.has(legacyId)) continue;
      if (map.has(legacyId) && map.get(legacyId) !== column.id) ambiguous.add(legacyId);
      map.set(legacyId, column.id);
    }
  }
  for (const legacyId of ambiguous) map.delete(legacyId);

  return map.size === 0 ? emptyLegacyColumnIdMap : map;
}

/**
 * Returns `previous` when `next` has the same entries, so state resolved with the map keeps its
 * identity when the columns change without changing their legacy IDs.
 */
export function reuseLegacyColumnIdMap(
  previous: LegacyColumnIdMap,
  next: LegacyColumnIdMap,
): LegacyColumnIdMap {
  if (previous.size !== next.size) return next;
  for (const [legacyId, columnId] of next) {
    if (previous.get(legacyId) !== columnId) return next;
  }
  return previous;
}

export function resolveLegacyColumnIdKeys<T>(
  record: Record<string, T>,
  map: LegacyColumnIdMap,
): Record<string, T> {
  if (map.size === 0 || !Object.keys(record).some((key) => map.has(key))) return record;

  const resolved: Record<string, T> = {};
  for (const [key, value] of Object.entries(record)) {
    const columnId = map.get(key);
    if (columnId === undefined) {
      resolved[key] = value;
    } else if (!(columnId in record)) {
      resolved[columnId] = value;
    }
  }
  return resolved;
}

export function resolveLegacyColumnIdList(ids: string[], map: LegacyColumnIdMap): string[] {
  if (map.size === 0 || !ids.some((id) => map.has(id))) return ids;

  const seen = new Set<string>();
  const resolved: string[] = [];
  for (const id of ids) {
    const columnId = map.get(id) ?? id;
    if (seen.has(columnId)) continue;
    if (columnId !== id && ids.includes(columnId)) continue;
    seen.add(columnId);
    resolved.push(columnId);
  }
  return resolved;
}

export function resolveLegacyColumnIdEntries<T extends { id: string }>(
  entries: T[],
  map: LegacyColumnIdMap,
): T[] {
  if (map.size === 0 || !entries.some((entry) => map.has(entry.id))) return entries;

  const resolved: T[] = [];
  for (const entry of entries) {
    const columnId = map.get(entry.id);
    if (columnId === undefined) {
      resolved.push(entry);
    } else if (!entries.some((other) => other.id === columnId)) {
      resolved.push({ ...entry, id: columnId });
    }
  }
  return resolved;
}

/**
 * Applies a table updater to the owner's previous entries with legacy IDs moved to column IDs
 * first. TanStack's updaters find an entry by column ID, so a header toggle on a column whose sort
 * is saved under a legacy ID changes that sort as it would one saved under the column ID.
 */
export function resolveLegacyColumnIdUpdater<T extends { id: string }>(
  updater: Updater<T[]>,
  map: LegacyColumnIdMap,
): (previous: T[]) => T[] {
  return (previous) => functionalUpdate(updater, resolveLegacyColumnIdEntries(previous, map));
}
